import { useQuery } from '@tanstack/react-query';
import { Link, Outlet } from '@tanstack/react-router';
import { sessionQuery } from '../lib/session.js';
import { resolveTimeZone } from '../lib/time-zone.js';
import { CompanyOutbox } from './company-outbox.js';
import { EmailTemplates } from './email-templates.js';
import type { OutboxView, TemplatesView } from './list-views.js';
import type { ViewSetter } from './view-state.js';

/// No console global não há empresa: o fuso é o de quem está lendo, e só depois o do navegador.
/// Datar a fila em UTC estaria certo e pareceria errado.
function usePlatformTimeZone(): string {
  const session = useQuery(sessionQuery);
  return resolveTimeZone(session.data?.timeZone, null);
}

/// Comunicação: três ângulos do mesmo assunto.
///
/// Caixa de saída é o que já saiu; Modelos é o texto que vai sair; Ajustes é de onde saem o
/// remetente e os prazos. Ficam juntos porque a mensagem grava o número da versão que a escreveu —
/// quem estranha um e-mail entregue vai querer, no mesmo lugar, ver qual texto foi usado.
///
/// As abas são links, e não botões, porque cada uma é um endereço: quem quiser abrir Modelos numa
/// aba nova do navegador consegue, e o gesto de voltar volta uma aba, não a tela inteira.
export function ConsoleCommunication() {
  const session = useQuery(sessionQuery).data;
  const platform = session?.context?.kind === 'PLATFORM' ? session.context : null;
  const can = (permission: string) =>
    session?.platformRole === 'ROOT' || platform?.permissions.includes(permission);
  const tabs = [
    ...(can('platform.email_messages.read')
      ? [{ to: '/communication/outbox' as const, label: 'Caixa de saída' }]
      : []),
    ...(can('platform.email_templates.read')
      ? [{ to: '/communication/templates' as const, label: 'Modelos' }]
      : []),
    ...(can('platform.communication_settings.read')
      ? [{ to: '/communication/settings' as const, label: 'Ajustes' }]
      : []),
  ] as const;

  return (
    <div>
      <div
        role="tablist"
        aria-label="Comunicação"
        className="mb-7 flex flex-wrap border-b border-surface-border"
      >
        {tabs.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            role="tab"
            activeProps={{
              'aria-selected': true,
              className:
                'border-b-2 border-brand-400 px-4 py-3 text-sm font-semibold text-brand-400',
            }}
            inactiveProps={{
              'aria-selected': false,
              className:
                'border-b-2 border-transparent px-4 py-3 text-sm font-semibold text-content-muted transition hover:text-content',
            }}
          >
            {item.label}
          </Link>
        ))}
      </div>
      <Outlet />
    </div>
  );
}

export function ConsoleOutbox({
  view,
  onView,
}: {
  view: OutboxView;
  onView: ViewSetter<OutboxView>;
}) {
  return (
    <CompanyOutbox timeZone={usePlatformTimeZone()} scope="platform" view={view} onView={onView} />
  );
}

export function ConsoleTemplates({
  view,
  onView,
}: {
  view: TemplatesView;
  onView: ViewSetter<TemplatesView>;
}) {
  return <EmailTemplates timeZone={usePlatformTimeZone()} view={view} onView={onView} />;
}
